import { buildEditalContextForIa } from '@/lib/buildEditalContextForIa';

/** Schema JSON esperado pelo invoke-llm (instrução reforçada no prompt). */
export const SCHEMA_IMPUGNACAO_IA = {
  type: 'object',
  properties: {
    resumo_executivo: {
      type: 'string',
      description:
        '2–4 frases assertivas: síntese dos vícios ou riscos competitivos identificados nos PDFs e por que levam à impugnação (tom técnico e firme, não neutro)',
    },
    confianca_0_100: {
      type: 'number',
      description: 'Grau de confiança da análise dado à completude dos dados (0–100)',
    },
    campos_edital_utilizados: {
      type: 'number',
      description:
        'Quantidade aproximada de trechos ou cláusulas dos PDFs analisados em que a análise se apoiou',
    },
    irregularidades: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Identificador curto estável, ex.: irr-1' },
          titulo: { type: 'string' },
          severidade: { type: 'string', description: 'Alta, Média ou Baixa' },
          tipo_vicio: {
            type: 'string',
            description: 'Ex.: restrição competitiva, direcionamento, desproporcionalidade, ilegalidade manifesta',
          },
          trecho_ou_fato_edital: {
            type: 'string',
            description: 'Citação ou paráfrase fiel do que consta nos dados (campo/cláusula)',
          },
          fundamento_legal: {
            type: 'string',
            description: 'Artigos da Lei 14.133/2021 e atos infralegais pertinentes, sem inventar número de processo',
          },
          referencia_tcu_sumula_acordao: {
            type: 'string',
            description:
              'Referência a súmula ou linha jurisprudencial do TCU quando houver correspondência temática; se não houver base segura nos dados, indique "conforme jurisprudência consolidada do TCU sobre [tema]" sem número inventado',
          },
          pedido_correcao: {
            type: 'string',
            description:
              'Pedido contundente de adequação/reforma/supressão da cláusula ou exigência (impugnação), com redação forense clara — não apenas “solicita esclarecimento”',
          },
        },
        required: ['titulo', 'severidade', 'trecho_ou_fato_edital', 'fundamento_legal', 'pedido_correcao'],
      },
    },
    checklist_conformidade: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          item: { type: 'string' },
          atendido: { type: 'boolean' },
          observacao: { type: 'string' },
        },
        required: ['item', 'atendido'],
      },
    },
    bases_juridicas_citadas: {
      type: 'array',
      items: { type: 'string' },
      description: 'Lista de dispositivos lei 14.133/2021 e referências TCU/doutrina usadas na minuta',
    },
    cabecalho_institucional: {
      type: 'string',
      description:
        'Endereçamento e epígrafe extraídos SOMENTE do texto dos PDFs (edital e anexos): cargo (Pregoeiro(a) ou Agente de Contratação) e nome quando constarem; nome do órgão público; linha REF. com modalidade e número da licitação; linha PROCESSO ADMINISTRATIVO Nº quando houver. Formato obrigatório (uma linha em branco entre blocos):\n' +
        'AO ILUSTRÍSSIMO(A) SENHOR(A) [PREGOEIRO(A) / AGENTE DE CONTRATAÇÃO — nome se constar]\n\n' +
        '[NOME DO ÓRGÃO PÚBLICO]\n\n' +
        'REF.: [MODALIDADE E NÚMERO — ex.: PREGÃO ELETRÔNICO Nº XX/202X]\n\n' +
        'PROCESSO ADMINISTRATIVO Nº [número ou «não localizado no texto fornecido»].\n' +
        'Não incluir qualificação da empresa, nem «vem, respeitosamente», nem o título IMPUGNAÇÃO AO EDITAL — isso é montado pelo aplicativo após este bloco.',
    },
    minuta_impugnacao: {
      type: 'string',
      description:
        'CORPO jurídico após a qualificação do impugnante (inserida pelo sistema): somente as seções numeradas abaixo, nesta ordem, com títulos em MAIÚSCULAS como cabeçalho de cada bloco. Não repetir endereçamento ao órgão, REF., processo, CNPJ/razão social do impugnante. Lei aplicável: indicar expressamente Lei 14.133/2021 ou Lei 8.666/1993 conforme constar no instrumento convocatório analisado; se ambas ou a base legal forem ambíguas nos PDFs, fundamentar em ambas de forma compatível sem inventar dados.\n' +
        '1. DA TEMPESTIVIDADE — prazo legal e tempestividade perante o cronograma do edital (apenas com base no texto dos PDFs).\n' +
        '2. DOS FATOS — narrativa objetiva dos vícios e trechos impugnados (âncora nos PDFs).\n' +
        '3. DO MÉRITO / FUNDAMENTAÇÃO JURÍDICA — dispositivos legais, infralegal e linha TCU/doutrina pertinentes.\n' +
        '4. DOS PEDIDOS — pedidos numerados (recebimento, adequação/supressão/retificação do edital, publicação de retificação, intimação, efeitos úteis, etc.). Tom forense firme; pedidos de esclarecimento apenas subsidiários.',
    },
  },
  required: [
    'resumo_executivo',
    'irregularidades',
    'checklist_conformidade',
    'bases_juridicas_citadas',
    'cabecalho_institucional',
    'minuta_impugnacao',
  ],
};

const MAX_ANALISE_IA_APPEND = 48_000;

function appendAnaliseViabilidadeSeHouver(edital) {
  const de = edital?.dados_extraidos;
  const raw =
    (typeof de === 'object' && de?.analise_ia) || edital?.analise_ia || '';
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return '';
  const slice = s.length > MAX_ANALISE_IA_APPEND ? `${s.slice(0, MAX_ANALISE_IA_APPEND)}\n… [texto truncado]` : s;
  return `\n\n--- Relatório de viabilidade já existente no Auction (use como insumo; não contradizer fatos do JSON acima) ---\n${slice}`;
}

/**
 * Prompt do usuário para invoke-llm: instruções + dados do edital.
 */
export function buildImpugnacaoIaPrompt(edital, editalLabel) {
  const context = buildEditalContextForIa(edital);
  const extra = appendAnaliseViabilidadeSeHouver(edital);
  const rotulo = editalLabel || edital?.titulo_licitacao || edital?.identificacao || 'Edital em análise';

  return `Você é advogado especialista em licitações públicas brasileiras, com atuação contundente em IMPUGNAÇÃO DE EDITAL (não em mero pedido de esclarecimento).

Postura obrigatória
- O produto principal é a IMPUGNAÇÃO: identificar com rigor tudo nos dados que autorize contestação ao instrumento convocatório (restrição à concorrência, direcionamento, desproporcionalidade, exigências injustificadas, ilegalidades, vícios de competência ou de forma, critérios vagos, incompatibilidade TR/objeto, etc.).
- Seja ofensivo na análise dentro da técnica: explore ao máximo ângulos que sustentem a reforma ou adequação do edital, sempre ancorados no contexto.
- Pedidos de esclarecimento são EXCEÇÃO: use apenas quando não houver como impugnar com segurança mas houver obscuridade material relevante; nunca use tom passivo ou de “dúvida educada” como saída principal quando há fundamento para impugnar.

Missão
1) Analisar automaticamente os DADOS DO EDITAL (JSON abaixo) e identificar irregularidades, cláusulas restritivas, direcionamentos e ilegalidades com impacto na competitividade.
2) Fundamentar na Lei nº 14.133/2021 e/ou Lei nº 8.666/1993 conforme o regime indicado no contexto do edital, decretos e normas correlatas, linha do TCU e doutrina — sem inventar números de processo, datas de julgamento ou trechos inexistentes no contexto.
3) Produzir cabecalho_institucional (órgão, pregoeiro/agente, REF., processo) e minuta_impugnacao nas seções 1 a 4 (Tempestividade, Fatos, Mérito, Pedidos), conforme schema; o aplicativo acrescenta entre eles a qualificação da empresa.

Regras anti-alucinação (obrigatórias)
- Use apenas fatos e trechos sustentados nos dados fornecidos. Se algo não constar, diga "não informado nos dados extraídos" ou "requer confirmação no PDF integral".
- Não cite acórdãos ou súmulas com número específico salvo quando for referência genérica e segura; prefira "conforme entendimento reiterado do TCU sobre [tema]".
- Cada irregularidade deve ancorar em "trecho_ou_fato_edital" com base em campos do JSON (ou no relatório de viabilidade anexo, se for fiel a ele).
- Se não houver qualquer âncora para vício, irregularidades pode ser [] e explique no resumo; a minuta deve ainda assim defender a competitividade com o que for possível (sem inventar fatos). Evite preencher a peça só com esclarecimentos genéricos.

Qualidade da minuta (estrutura básica obrigatória — Lei 14.133/2021 ou Lei 8.666/1993 conforme o edital)
- Campo cabecalho_institucional: endereçamento ao pregoeiro ou agente de contratação, nome do órgão, REF. (modalidade e número) e processo administrativo, extraídos do JSON do edital quando existirem; se ausente, use placeholders explícitos «a confirmar no edital» sem inventar.
- Campo minuta_impugnacao: apenas o corpo após a qualificação que o sistema insere do cadastro da empresa — obrigatoriamente nas seções «1. DA TEMPESTIVIDADE», «2. DOS FATOS», «3. DO MÉRITO / FUNDAMENTAÇÃO JURÍDICA», «4. DOS PEDIDOS», nesta ordem, com conteúdo técnico em cada uma.
- Fundamentar na lei aplicável ao certame (14.133/2021 ou 8.666/1993) conforme constar nos dados; se o instrumento indicar claramente um regime, priorize-o; cite artigos pertinentes sem inventar número de processo.
- Tom forense contundente, claro e objetivo (impugnação, não consulta informal).

Processo licitatório (rótulo): ${rotulo}

--- DADOS DO EDITAL (JSON) ---
${context || '{}'}${extra}

Retorne estritamente o JSON conforme o schema solicitado (sem markdown, sem texto fora do JSON).`;
}

/**
 * Análise exclusivamente a partir do texto extraído dos PDFs enviados (seção "Documento" na requisição ao invoke-llm).
 * Não utiliza JSON do edital processado em Novo Edital/Cotação.
 */
export function buildImpugnacaoIaPromptFromDocumentos(
  editalLabel,
  documentosEnviadosResumo,
  sugestaoImpugnacaoOpcional = ''
) {
  const rotulo = editalLabel || 'Processo licitatório em análise';
  const listaDoc =
    documentosEnviadosResumo && String(documentosEnviadosResumo).trim()
      ? String(documentosEnviadosResumo).trim()
      : '(documentos não listados)';

  const sugestao = String(sugestaoImpugnacaoOpcional || '').trim();
  const blocoSugestao = sugestao
    ? `
Orientação do licitante (incorporar quando compatível com o texto dos PDFs)
O usuário solicitou priorizar ou enfatizar os seguintes ângulos na análise e na minuta:
${sugestao}

Regras para uso desta orientação:
- Priorize argumentação de IMPUGNAÇÃO alinhada à sugestão quando puder ampará-la em trechos reais dos PDFs (citação ou paráfrase fiel).
- Não invente cláusulas, certificações ou exigências que não apareçam nos documentos.
- Se a sugestão não tiver base no texto, declare no resumo_executivo; na minuta use tese subsidiária, impugnação condicionada ou pedido de retificação/esclarecimento apenas como apoio — não como substituto da linha principal de impugnação quando outros vícios estiverem ancorados nos PDFs.
`
    : '';

  return `Você é advogado especialista em licitações públicas brasileiras, com postura contundente em IMPUGNAÇÃO DE EDITAL. O entregável central é a IMPUGNAÇÃO (contestação ao instrumento convocatório com pedidos de adequação/reforma), não um memorial de dúvidas ou pedidos de esclarecimento como eixo principal.

Fonte da análise (obrigatório)
- Baseie-se EXCLUSIVAMENTE no texto extraído dos PDFs na seção "Documento" (um ou mais arquivos concatenados).
- NÃO use nem invente dados de sistemas ou bases externas não presentes no texto dos PDFs.
- Se o texto estiver incompleto ou ilegível, declare no resumo_executivo e reduza confianca_0_100.

Exploração dos PDFs (obrigatório)
- Leia de forma ofensiva e sistemática: exigências de habilitação e qualificação, especificações, critérios de julgamento, prazos, exclusividade, marcas/modelos, certificações, amostras, amostragem de preços, TR, contratações integradas, cotas, lotes, cláusulas de vigência e execução — tudo que possa indicar restrição indevida, direcionamento, desproporcionalidade, arbitrariedade ou violação à isonomia e à competitividade.
- Quando houver indício plausível ancorado no texto, classifique com severidade compatível e formule pedido de correção na impugnação. Não suavize vícios com linguagem de “pedido de esclarecimento” se o texto sustenta impugnação de mérito.

Documentação recebida para análise (metadado do usuário):
${listaDoc}

Processo (rótulo informado pelo usuário): ${rotulo}
${blocoSugestao}
Estrutura básica obrigatória da peça (alinhar-se a ela)
A) Campo cabecalho_institucional — extrair exclusivamente dos PDFs (capa, preâmbulo, cláusulas iniciais do edital), sem inventar:
   AO ILUSTRÍSSIMO(A) SENHOR(A) [PREGOEIRO(A) / AGENTE DE CONTRATAÇÃO — nome e cargo se constarem]
   [linha em branco]
   [NOME DO ÓRGÃO PÚBLICO]
   [linha em branco]
   REF.: [MODALIDADE E NÚMERO DA LICITAÇÃO — ex.: PREGÃO ELETRÔNICO Nº XX/202X]
   [linha em branco]
   PROCESSO ADMINISTRATIVO Nº [XXXX/XXXX ou «não localizado no texto fornecido»]
B) O aplicativo inserirá, após (A), o parágrafo de qualificação da empresa (razão social, CNPJ, endereço, representante) e o título «IMPUGNAÇÃO AO EDITAL» com a frase de abertura («contra os termos… pelos motivos…»).
C) Campo minuta_impugnacao — somente o corpo após essa abertura, obrigatoriamente nesta ordem e com estes títulos em linha própria:
   1. DA TEMPESTIVIDADE
   2. DOS FATOS
   3. DO MÉRITO / FUNDAMENTAÇÃO JURÍDICA
   4. DOS PEDIDOS
   Em (3), fundamente na Lei nº 14.133/2021 ou na Lei nº 8.666/1993 conforme constar no instrumento convocatório nos PDFs; se o edital indicar claramente um regime, priorize-o; se houver ambiguidade, fundamente de forma compatível com ambas sem afirmar fatos inexistentes.

Missão
1) Identificar o máximo de irregularidades e riscos competitivos sustentados no texto dos PDFs (com âncora fiel em trecho_ou_fato_edital).
2) Fundamentar nas leis e normas aplicáveis ao certame conforme o texto dos PDFs, linha do TCU e doutrina — sem inventar números de processo, datas ou trechos inexistentes.
3) Produzir cabecalho_institucional conforme (A) e minuta_impugnacao conforme (C). NÃO inclua no JSON qualificação do impugnante (CNPJ, sede, representante), nem «vem, respeitosamente» antes da qualificação, nem duplique o título IMPUGNAÇÃO AO EDITAL — isso é montado pelo aplicativo.

Pedidos de esclarecimento
- São subsidiários: inclua apenas quando, para um ponto específico, o PDF for genuinamente obscuro e não permitir impugnar com segurança, ou como reforço após os pedidos principais de impugnação. Não estruture a minuta como se o objetivo fosse apenas “esclarecer dúvidas”.

Regras anti-alucinação
- Cada irregularidade: "trecho_ou_fato_edital" deve citar ou parafrasear fielmente trecho dos PDFs (indique documento N pela ordem dos arquivos quando possível).
- Não cite acórdão/súmula com número inventado; use linha genérica segura do TCU quando necessário.
- Se não houver âncora mínima para nenhum vício, irregularidades pode ser [] e explique no resumo; a minuta não deve ser apenas lista genérica de esclarecimentos sem fundamento no texto.

Retorne estritamente o JSON conforme o schema solicitado (sem markdown, sem texto fora do JSON).`;
}
